/**
 * Links that open a record in BCE's Salesforce org (Lightning). One origin
 * and one URL shape for every page that links out, so the org address lives
 * in a single place.
 */

import { getRuntimeConfig } from '@/lib/runtime-config';

/** Production's Lightning origin, used unless this environment names another org. */
export const SALESFORCE_ORIGIN = 'https://bestcorporateevents.lightning.force.com';

/** The org this environment's links open in: staging's sandbox, else production. */
export function salesforceOrigin(): string {
  return (getRuntimeConfig('SALESFORCE_ORIGIN') ?? SALESFORCE_ORIGIN).replace(/\/+$/, '');
}

/** The Salesforce object API names the app links to. */
export const SALESFORCE_OBJECT = {
  opportunity: 'Opportunity',
  statement: 'Income_and_Expense_Statement__c',
  expense: 'Expense__c',
} as const;
export type SalesforceObject = (typeof SALESFORCE_OBJECT)[keyof typeof SALESFORCE_OBJECT];

/** A record's Lightning view page. */
export function salesforceRecordUrl(object: SalesforceObject, id: string): string {
  return `${salesforceOrigin()}/lightning/r/${object}/${encodeURIComponent(id)}/view`;
}

export function salesforceOpportunityUrl(opportunityId: string): string {
  return salesforceRecordUrl(SALESFORCE_OBJECT.opportunity, opportunityId);
}

/** The event's Income & Expense Statement. */
export function salesforceStatementUrl(statementId: string): string {
  return salesforceRecordUrl(SALESFORCE_OBJECT.statement, statementId);
}

/** One expense line on a statement. */
export function salesforceExpenseUrl(expenseId: string): string {
  return salesforceRecordUrl(SALESFORCE_OBJECT.expense, expenseId);
}
